import { useQueryClient } from "@tanstack/react-query";

import { industryQueryKeys } from "app/pages/sales-intelligence/modules/industry/queries/queryKeyFactory";

import type { CrmEnrichmentJob } from "../types/types";

type HookQueryKeys = {
    jobs: () => readonly string[];
    job: (id: string) => readonly string[];
};

type HookValue = {
    updateExistingJobInCache: (data: CrmEnrichmentJob) => void;
    addNewJobToCache: (data: CrmEnrichmentJob) => void;
    removeJobFromCache: (data: CrmEnrichmentJob) => void;
};

export const useCrmEnrichmentJobCache = (queryKeys: HookQueryKeys): HookValue => {
    const queryClient = useQueryClient();

    // A job's industry mapping drives the legacy-taxonomy banner, whose flag comes from a separate query;
    // refresh it on every job write so the banner reflects the change (see CrmIndustryTransitionAlertBanner).
    const invalidateCrmJobsTransition = (): void => {
        void queryClient.invalidateQueries({ queryKey: industryQueryKeys.crmJobsTransition() });
    };

    const updateExistingJobInCache = (data: CrmEnrichmentJob): void => {
        queryClient.setQueryData(queryKeys.jobs(), (jobs: CrmEnrichmentJob[] | undefined) => {
            if (!jobs) {
                return;
            }

            return jobs.map((job) => (job.id === data.id ? data : job));
        });

        queryClient.setQueryData(queryKeys.job(data.id), data);

        invalidateCrmJobsTransition();
    };

    const addNewJobToCache = (data: CrmEnrichmentJob): void => {
        queryClient.setQueryData(queryKeys.jobs(), (jobs: CrmEnrichmentJob[] | undefined) => {
            return jobs ? [...jobs, data] : [data];
        });

        queryClient.setQueryData(queryKeys.job(data.id), data);

        invalidateCrmJobsTransition();
    };

    const removeJobFromCache = (data: CrmEnrichmentJob): void => {
        queryClient.setQueryData(queryKeys.jobs(), (jobs: CrmEnrichmentJob[] | undefined) => {
            if (!jobs) {
                return;
            }

            return jobs.filter((jobItem) => jobItem.id !== data.id);
        });

        invalidateCrmJobsTransition();
    };

    return {
        updateExistingJobInCache,
        addNewJobToCache,
        removeJobFromCache,
    };
};
